import { useState } from "react";
import { TextInput } from "risa-oasis-ui_v2";
import CustomCheckBox from "../../../../components/customCheckBox/customCheckBox";
import { Question } from "../../types";

export interface TableColumn {
  id: string;
  title: string;
  type: string;
  width?: number;
}

export interface TableSchema {
  columns: TableColumn[];
  max_rows: number;
}

export interface TabularQuestionProps {
  question: Question & {
    table_schema?: TableSchema;
    answer?: any[];
  };
  onAnswerChange: (questionId: string, answer: any) => void;
  questionNumber?: string;
}

const TabularQuestion = (props: TabularQuestionProps) => {
  const { question, onAnswerChange, questionNumber = "1" } = props;
  const tableSchema = question.table_schema;

  const getColumnWidths = (columns: TableColumn[]) => {
    const checkboxColumns = columns.filter((col) => col.type === "checkbox");
    const otherColumns = columns.filter((col) => col.type !== "checkbox");
    const checkboxWidth = 10; // Fixed width for checkbox columns
    const totalCheckboxWidth = checkboxColumns.length * checkboxWidth;
    const remainingWidth = 100 - totalCheckboxWidth;
    const otherColumnWidth =
      otherColumns.length > 0 ? remainingWidth / otherColumns.length : 0;

    return columns.map((column) => ({
      ...column,
      width: column.type === "checkbox" ? checkboxWidth : otherColumnWidth,
    }));
  };

  const columnsWithWidths = tableSchema
    ? getColumnWidths(tableSchema.columns)
    : [];

  const [tableData, setTableData] = useState<Record<string, any>[]>(() => {
    if (question.answer && Array.isArray(question.answer)) {
      return question.answer;
    }

    const emptyRows: Record<string, any>[] = [];
    for (let i = 0; i < (tableSchema?.max_rows || 3); i++) {
      const row: Record<string, any> = {};
      tableSchema?.columns.forEach((column) => {
        if (column.type === "checkbox") {
          row[column.id] = false;
        } else {
          row[column.id] = "";
        }
      });
      emptyRows.push(row);
    }
    return emptyRows;
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    const newTableData = [...tableData];
    newTableData[rowIndex] = {
      ...newTableData[rowIndex],
      [columnId]: value,
    };
    setTableData(newTableData);
    onAnswerChange(question.id, newTableData);
  };

  if (!tableSchema) {
    return <div>No table schema provided</div>;
  }

  return (
    <div className="tabular-question">
      <h3 className="mb-4 text-sm font-semibold text-primaryGray-1">
        <span className="text-primaryBlue-1 mr-2 font-semibold">
          {questionNumber}.
        </span>
        {question.question}
      </h3>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr
              className="bg-primaryGray-16"
              style={{ display: "flex", width: "100%" }}
            >
              {columnsWithWidths.map((column) => (
                <th
                  key={column.id}
                  className={`p-4 text-xs text-gray-700 ${
                    column.type === "checkbox" ? "text-center" : "text-left"
                  }`}
                  style={{ width: `${column.width}%` }}
                >
                  {column.type !== "checkbox" ? column.title : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="rounded-xl border-b border-gray-300">
            {tableData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-x border-gray-300 hover:bg-gray-50"
                style={{ display: "flex", width: "100%" }}
              >
                {columnsWithWidths.map((column) => (
                  <td
                    key={column.id}
                    className="p-4"
                    style={{ width: `${column.width}%` }}
                  >
                    {column.type === "checkbox" ? (
                      <div className="flex justify-center">
                        <CustomCheckBox
                          id={`${column.id}-${rowIndex}`}
                          label=""
                          isChecked={row[column.id] || false}
                          onCheckBoxValueChange={(e: {
                            name: string;
                            value: boolean;
                          }) => handleCellChange(rowIndex, column.id, e.value)}
                        />
                      </div>
                    ) : (
                      <TextInput
                        id={column.id}
                        label=""
                        hideLabel={true}
                        defaultValue={row[column.id] || ""}
                        onChange={(e: { value: string }) =>
                          handleCellChange(rowIndex, column.id, e.value)
                        }
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TabularQuestion;
